'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import { ChevronLeft, ChevronRight, Maximize2, Newspaper, Quote, Tv, X } from 'lucide-react';
import { PRESS_ITEMS, type PressCard } from '@/content/founder';
import ScrollReveal from './ScrollReveal';

const AUTO_ADVANCE_MS = 6000;

function PressHighlight({ item }: { item: PressCard }) {
  return (
    <div className="mt-1.5 flex  w-full md:w-[70%] gap-3 rounded-[14px] border border-white/[0.18] bg-[#29334D]/80 p-4 backdrop-blur-[8px]">
      <Quote aria-hidden="true" className="mt-0.5 h-[22px] w-[22px] shrink-0 fill-[#A9B4FE] text-[#A9B4FE]" />
      <div>
        {item.quote ? (
          <blockquote className="font-Schibsted_Grotesk text-sm italic leading-relaxed text-white">
            &ldquo;{item.quote}&rdquo;
          </blockquote>
        ) : (
          <p className="font-Schibsted_Grotesk text-sm italic leading-relaxed text-white">{item.summary}</p>
        )}
        <p className="mt-2 font-Schibsted_Grotesk text-[13px] leading-relaxed text-[#C8D1E8]">
          {item.quoteAttribution || item.outlet}
        </p>
      </div>
    </div>
  );
}

export default function PressMedia() {
  const outletListRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [timerId, setTimerId] = useState<ReturnType<typeof setInterval> | null>(null);

  // Scroll only the horizontal outlet list, without moving the page.
  useEffect(() => {
    const list = outletListRef.current;
    const active = list?.children[index] as HTMLElement | undefined;
    if (!list || !active || window.matchMedia('(min-width: 1024px)').matches) return;
    const listBounds = list.getBoundingClientRect();
    const activeBounds = active.getBoundingClientRect();
    list.scrollTo({
      left: list.scrollLeft + activeBounds.left - listBounds.left - (list.clientWidth - activeBounds.width) / 2,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  }, [index]);

  const goTo = useCallback((i: number) => {
    setIndex(((i % PRESS_ITEMS.length) + PRESS_ITEMS.length) % PRESS_ITEMS.length);
  }, []);

  const restartTimer = useCallback(() => {
    setTimerId((current) => {
      if (current) clearInterval(current);
      return null;
    });
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % PRESS_ITEMS.length);
    }, AUTO_ADVANCE_MS);
    setTimerId(id);
  }, []);

  useEffect(() => {
    restartTimer();
    return () => {
      setTimerId((current) => {
        if (current) clearInterval(current);
        return null;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-advance pauses while the lightbox is open, resumes once it closes.
  useEffect(() => {
    if (lightboxOpen && timerId) {
      clearInterval(timerId);
      setTimerId(null);
    } else if (!lightboxOpen && !timerId) {
      restartTimer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen]);

  // Locks background scroll while the lightbox is open.
  useEffect(() => {
    if (!lightboxOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [lightboxOpen]);

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const handleManual = (i: number) => {
    goTo(i);
    restartTimer();
  };

  const goToNextWithImage = (direction: 1 | -1) => {
    let i = index;
    do {
      i = ((i + direction) % PRESS_ITEMS.length + PRESS_ITEMS.length) % PRESS_ITEMS.length;
    } while (!PRESS_ITEMS[i].image && i !== index);
    setIndex(i);
  };

  const featured = PRESS_ITEMS[index];

  return (
    <section id="press" className="relative overflow-hidden bg-gradient-to-b from-[#F7F9FF] to-[#EEF4FF] px-5 py-16 sm:px-8 sm:py-24 lg:py-[104px]">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 -top-28 h-[640px] w-[640px] rounded-full"
        style={{ background: 'radial-gradient(circle,rgba(25,90,255,0.14),transparent 65%)' }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-60 -left-52 h-[560px] w-[560px] rounded-full"
        style={{ background: 'radial-gradient(circle,rgba(169,180,254,0.25),transparent 65%)' }}
      />

      <div className="relative mx-auto max-w-[1320px]">
        <ScrollReveal className="mb-9 flex flex-wrap items-end justify-between gap-5 sm:mb-11">
          <div>
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-[#195AFF]" />
              <span className="font-Schibsted_Grotesk text-[13px] font-bold uppercase tracking-[0.22em] text-[#195AFF]">
                Press &amp; Media
              </span>
            </div>
            <h2 className="mt-3.5 font-NeueMontreal text-[36px] font-extrabold leading-[1.04] tracking-[-0.03em] text-[#090D28] sm:text-[60px]">
              Featured in{' '}
              <span className="bg-gradient-to-r from-[#195AFF] to-[#1586FF] bg-clip-text text-transparent font-NeueMontreal">
                the Press
              </span>
            </h2>
          </div>
          <p className="max-w-[440px] font-Schibsted_Grotesk text-[17px] leading-relaxed text-[#4A5570]">
            Our journey, vision and impact have been featured in leading newspapers, magazines, TV shows and online
            media across India and beyond.
          </p>
        </ScrollReveal>

        <div className="grid items-stretch gap-4 lg:grid-cols-[320px_1fr] lg:gap-7">
          {/* Outlet logos: horizontal scroll on mobile, vertical stack on desktop. */}
          <div ref={outletListRef} className="flex gap-2.5 overflow-x-auto pb-1 [scrollbar-width:none] lg:flex-col lg:overflow-visible lg:pb-0">
            {PRESS_ITEMS.map((item, i) => {
              const active = i === index;
              return (
                <button
                  key={item.outlet}
                  type="button"
                  onClick={() => handleManual(i)}
                  aria-current={active}
                  aria-label={`Show ${item.outlet}`}
                  className={`grid w-[280px] shrink-0 grid-cols-[64px_minmax(0,1fr)] items-center gap-x-3.5 gap-y-0.5 rounded-2xl border px-4 py-3.5 text-left transition-colors [-webkit-tap-highlight-color:transparent] lg:w-auto lg:px-5 ${
                    active ? 'border-[#195AFF] bg-white' : 'border-[#E6EAF3] bg-white/50 hover:bg-white'
                  }`}
                >
                  <span className={`row-span-2 flex h-12 w-16 items-center justify-center rounded-lg p-1.5 ${item.outlet === 'Valar Thozhil' ? 'bg-black' : item.outlet === 'The Indian Express' ? 'bg-[#090D28]' : 'bg-white'}`}>
                    {item.logo ? (
                      <Image src={item.logo} alt="" width={64} height={48} className="h-full w-full object-contain" />
                    ) : (
                      <Tv aria-hidden="true" className="h-7 w-7 text-[#195AFF]" />
                    )}
                  </span>
                  <span
                    className={`truncate font-NeueMontreal text-[19px] font-bold leading-tight ${
                      active ? 'text-[#195AFF]' : 'text-[#090D28]'
                    }`}
                  >
                    {item.outlet}
                  </span>
                  <span className={`col-start-2 font-Schibsted_Grotesk text-xs font-bold uppercase tracking-[0.1em] ${active ? 'text-[#195AFF]' : 'text-[#596278]'}`}>
                    {item.meta}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Big image panel for the active press item. */}
          <article className="relative min-h-[520px] overflow-hidden rounded-3xl border border-[#E6EAF3] bg-[#EEF4FF] shadow-[0_40px_80px_-40px_rgba(9,13,40,0.4)] lg:min-h-[560px]">
            {featured.image ? (
              <Image
                key={featured.headline}
                src={featured.image}
                alt={featured.imageAlt ?? featured.headline}
                fill
                sizes="(min-width: 1024px) 65vw, 100vw"
                className="object-cover object-top"
              />
            ) : (
              <div className="absolute inset-x-0 top-10 flex h-[220px] items-center justify-center p-8">
                {featured.logo ? (
                  <Image src={featured.logo} alt={featured.outlet} width={280} height={140} className="h-full w-full max-w-[280px] object-contain" />
                ) : (
                  <Newspaper aria-hidden="true" className="h-16 w-16 text-[#BFD2FF]" />
                )}
              </div>
            )}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  'linear-gradient(180deg,rgba(6,18,51,0) 34%,rgba(6,18,51,0.72) 64%,rgba(6,18,51,0.94) 100%)',
              }}
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col gap-3 p-6 sm:p-9">
            
              <h3 className="max-w-[640px] font-NeueMontreal text-2xl font-bold leading-[1.12] tracking-[-0.02em] text-white sm:text-[34px]">
                {featured.headline}
              </h3>
              <PressHighlight item={featured} />
            </div>

            <div className="absolute right-4 top-4 flex gap-2">
              <button
                type="button"
                onClick={() => handleManual(index - 1)}
                aria-label="Previous"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E6EAF3] bg-white/90 text-[#195AFF] shadow-[0_10px_24px_-10px_rgba(9,13,40,0.3)] backdrop-blur-[6px] transition-colors [-webkit-tap-highlight-color:transparent] hover:bg-[#195AFF] hover:text-white"
              >
                <ChevronLeft aria-hidden="true" className="h-[18px] w-[18px]" />
              </button>
              <button
                type="button"
                onClick={() => handleManual(index + 1)}
                aria-label="Next"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E6EAF3] bg-white/90 text-[#195AFF] shadow-[0_10px_24px_-10px_rgba(9,13,40,0.3)] backdrop-blur-[6px] transition-colors [-webkit-tap-highlight-color:transparent] hover:bg-[#195AFF] hover:text-white"
              >
                <ChevronRight aria-hidden="true" className="h-[18px] w-[18px]" />
              </button>
            </div>

            {featured.image && (
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                aria-label="View full clipping"
                className="absolute bottom-4 right-4 flex h-11 items-center gap-2 rounded-full border border-[#E6EAF3] bg-white/90 px-4 font-Schibsted_Grotesk text-[13px] font-bold text-[#195AFF] shadow-[0_10px_24px_-10px_rgba(9,13,40,0.3)] backdrop-blur-[6px] transition-colors [-webkit-tap-highlight-color:transparent] hover:bg-[#195AFF] hover:text-white sm:px-4"
              >
                <Maximize2 aria-hidden="true" className="h-4 w-4" />
                <span className="hidden sm:inline  font-Schibsted_Grotesk">View full</span>
              </button>
            )}
          </article>
        </div>
      </div>

      {mounted && lightboxOpen && featured.image && createPortal(
        <div
          onClick={() => setLightboxOpen(false)}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#061233]/95 p-3 backdrop-blur-[6px] sm:p-10"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative grid w-full max-w-[1240px] grid-cols-1 overflow-y-auto rounded-[20px] bg-white shadow-[0_40px_100px_-30px_rgba(0,0,0,0.6)] max-h-[calc(100dvh-24px)] lg:max-h-[86vh] lg:grid-cols-[1.5fr_1fr] lg:overflow-hidden"
          >
            <div className="relative bg-[#0A1D57] lg:max-h-[86vh] lg:overflow-auto">
              <Image
                src={featured.image}
                alt={featured.imageAlt ?? featured.headline}
                width={1200}
                height={1500}
                className="block h-auto w-full object-contain"
              />
            </div>
            <div className="flex flex-col gap-3.5 p-6 sm:p-9 lg:max-h-[86vh] lg:overflow-auto">
              <span className="inline-flex w-fit rounded-full bg-[#E6EFFF] px-3 py-1.5 font-Schibsted_Grotesk text-xs font-bold uppercase tracking-[0.08em] text-[#195AFF]">
                {featured.outlet} · {featured.meta}
              </span>
              <h3 className="font-NeueMontreal text-2xl font-bold leading-tight tracking-[-0.02em] text-[#090D28] sm:text-[32px]">
                {featured.headline}
              </h3>
              <PressHighlight item={featured} />
              <div className="mt-auto flex items-center justify-between gap-3 pt-3">
                <span className="font-Schibsted_Grotesk text-[13px] font-bold text-[#596278]">
                  {index + 1} / {PRESS_ITEMS.length}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => goToNextWithImage(-1)}
                    aria-label="Previous"
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-[#CFDAF2] bg-white text-[#195AFF] transition-colors [-webkit-tap-highlight-color:transparent] hover:bg-[#195AFF] hover:text-white"
                  >
                    <ChevronLeft aria-hidden="true" className="h-[18px] w-[18px]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => goToNextWithImage(1)}
                    aria-label="Next"
                    className="flex h-11 w-11 items-center justify-center rounded-full bg-[#195AFF] text-white transition-colors [-webkit-tap-highlight-color:transparent] hover:bg-[#0F4AE6]"
                  >
                    <ChevronRight aria-hidden="true" className="h-[18px] w-[18px]" />
                  </button>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setLightboxOpen(false)}
              aria-label="Close"
              className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-[#090D28] shadow-[0_6px_16px_-6px_rgba(0,0,0,0.4)] [-webkit-tap-highlight-color:transparent]"
            >
              <X aria-hidden="true" className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}
